import { computed } from 'vue';

import type { Availability, OctoUnitPricing, SupportedCurrency } from '@/types/booking-api';
import type { BookingWidgetContext } from '@/types/booking-widget-context';
import type { BookingOptionUnit } from '@/types/products';

import { getLocalizedLabel } from '@/utils/utils';

export type PricingLine = {
  unitId: string;
  unitLabel: string;
  quantity: number;
  pricePerUnit: number;
  subtotal: number;
  currency: SupportedCurrency;
  currencyPrecision: number;
  originalPricePerUnit: number;
};

type ResolvedUnitPrice = {
  pricePerUnit: number;
  originalPricePerUnit: number;
  currency: SupportedCurrency;
  currencyPrecision: number;
};

function resolveUnitPrice(
  unit: BookingOptionUnit,
  sessionUnitPricing: OctoUnitPricing[] | undefined,
): ResolvedUnitPrice {
  if (sessionUnitPricing && sessionUnitPricing.length > 0) {
    const hotPricing = sessionUnitPricing.find(up => up.unitId === unit.id);
    if (hotPricing) {
      return {
        pricePerUnit: hotPricing.retail,
        originalPricePerUnit: hotPricing.original,
        currency: hotPricing.currency,
        currencyPrecision: hotPricing.currencyPrecision,
      };
    }
  }

  const currencyPrecision = unit.currencyPrecision || 2;
  return {
    pricePerUnit: unit.price * (10 ** currencyPrecision),
    originalPricePerUnit: (unit.originalPrice ?? unit.price) * (10 ** currencyPrecision),
    currency: unit.currency,
    currencyPrecision,
  };
}

export function useBookingPricing(context: BookingWidgetContext) {
  const pricingLines = computed((): PricingLine[] => {
    if (!context.bookingWidget.selectedOption.value || context.bookingWidget.selectedUnits.value.size === 0)
      return [];

    const lines: PricingLine[] = [];
    const option = context.bookingWidget.selectedOption.value;
    const selectedUnits = context.bookingWidget.selectedUnits.value;
    const selectedSession = context.bookingWidget.selectedSession.value;

    selectedUnits.forEach((quantity, unitId) => {
      if (quantity <= 0)
        return;

      const unit = option.units.find(u => u.id === unitId);
      if (!unit)
        return;

      const unitLabel = getLocalizedLabel(unit.labels, unit.label, context.bookingWidget.currentLocale.value);
      const resolved = resolveUnitPrice(unit, selectedSession?.unitPricing);

      lines.push({
        unitId,
        unitLabel,
        quantity,
        ...resolved,
        subtotal: resolved.pricePerUnit * quantity,
      });
    });

    return lines;
  });

  function getSessionPrice(session: Availability): { price: number; currency: SupportedCurrency; currencyPrecision: number } {
    const selectedUnits = context.bookingWidget.selectedUnits.value;
    const option = context.bookingWidget.selectedOption.value;

    if (!option || selectedUnits.size === 0) {
      return { price: 0, currency: 'EUR', currencyPrecision: 2 };
    }

    let total = 0;
    let currency: SupportedCurrency = 'EUR';
    let precision = 2;

    selectedUnits.forEach((quantity, unitId) => {
      if (quantity <= 0)
        return;
      const unit = option.units.find(u => u.id === unitId);
      if (!unit)
        return;

      const resolved = resolveUnitPrice(unit, session.unitPricing);
      total += resolved.pricePerUnit * quantity;
      currency = resolved.currency;
      precision = resolved.currencyPrecision;
    });

    return { price: total, currency, currencyPrecision: precision };
  }

  return {
    pricingLines,
    getSessionPrice,
  };
}
